import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import FilterBar from './components/FilterBar';
import KPICard from './components/KPICard';
import ReportChart from './components/ReportChart';
import ReportAnalysis from './components/ReportAnalysis';
import ExportButton from './components/ExportButton';
import { getDriverReports } from '../../../services/reportService';
import { CHART_COLORS } from './utils/chartConfig';
import { prepareKPIsForExport } from './utils/exportUtils';
import './Reports.css';

const DriverReports = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({});
    const [reportData, setReportData] = useState(null);

    const fetchReportData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getDriverReports(filters);
            setReportData(response.data);
        } catch (err) {
            console.error('Error fetching driver reports:', err);
            setError('Erreur lors du chargement des données. Veuillez réessayer.');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchReportData();
    }, [fetchReportData]);

    const revenueByDriverData = reportData?.revenueByDriver ? {
        labels: reportData.revenueByDriver.map(item => item.driverName),
        datasets: [{
            label: 'Chiffre d\'affaires',
            data: reportData.revenueByDriver.map(item => item.revenue),
            backgroundColor: CHART_COLORS.primary,
            borderRadius: 8
        }]
    } : null;

    const missionsByDriverData = reportData?.missionsByDriver ? {
        labels: reportData.missionsByDriver.map(item => item.driverName),
        datasets: [{
            label: 'Missions réalisées',
            data: reportData.missionsByDriver.map(item => item.count),
            backgroundColor: CHART_COLORS.success,
            borderRadius: 8
        }]
    } : null;

    const exportData = reportData ? [
        ...prepareKPIsForExport(reportData.kpis),
        { Indicateur: '', Valeur: '' },
        { Indicateur: 'CA par Conducteur (Top 10)', Valeur: '' },
        ...(reportData.revenueByDriver || []).map(item => ({ Indicateur: item.driverName, Valeur: item.revenue })),
        { Indicateur: '', Valeur: '' },
        { Indicateur: 'Missions par Conducteur', Valeur: '' },
        ...(reportData.missionsByDriver || []).map(item => ({ Indicateur: item.driverName, Valeur: item.count }))
    ] : null;

    return (
        <DashboardLayout>
            <div className="reports-page">
                <div className="reports-header">
                    <div>
                        <h1 className="reports-title">Rapports & Analytics</h1>
                        <p className="reports-subtitle">Rapports conducteurs</p>
                    </div>
                    <ExportButton
                        reportName="rapports_conducteurs"
                        data={exportData}
                        headers={['Indicateur', 'Valeur']}
                        keys={['Indicateur', 'Valeur']}
                        disabled={loading || !reportData}
                    />
                </div>

                <FilterBar
                    onFilterChange={setFilters}
                    availableFilters={['currency', 'period', 'driver', 'status']}
                />

                {error && (
                    <div className="alert alert-danger rounded-3 border-0 shadow-sm">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                    </div>
                )}

                {/* Analysis Section */}
                {reportData?.analysis && (
                    <ReportAnalysis analysis={reportData.analysis} />
                )}

                <div className="row g-4 mb-4">
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="CA total conducteurs"
                            value={reportData?.kpis?.totalRevenue}
                            previousValue={reportData?.kpis?.previousTotalRevenue}
                            format="currency"
                            currency={filters.currency || 'EUR'}
                            icon="bi bi-currency-euro"
                            gradient="blue"
                            loading={loading}
                        />
                    </div>
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="Missions réalisées"
                            value={reportData?.kpis?.totalMissions}
                            previousValue={reportData?.kpis?.previousTotalMissions}
                            format="number"
                            icon="bi bi-check-circle"
                            gradient="green"
                            loading={loading}
                        />
                    </div>

                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="CA moyen/conducteur"
                            value={reportData?.kpis?.avgRevenuePerDriver}
                            format="currency"
                            currency={filters.currency || 'EUR'}
                            icon="bi bi-person-check"
                            gradient="orange"
                            loading={loading}
                        />
                    </div>
                </div>

                <div className="row g-4">
                    <div className="col-lg-6">
                        <ReportChart
                            type="bar"
                            data={revenueByDriverData}
                            title="Chiffre d'affaires par conducteur (Top 10)"
                            height={350}
                            loading={loading}
                            id="revenue-by-driver-chart"
                            options={{ dataType: 'currency' }}
                        />
                    </div>
                    <div className="col-lg-6">
                        <ReportChart
                            type="bar"
                            data={missionsByDriverData}
                            title="Missions par conducteur (Top 10)"
                            height={350}
                            loading={loading}
                        />
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default DriverReports;
